import {useQuery} from '@apollo/client';
import {DatePicker, Form, Spin} from 'antd';
import {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  GET_APP_CONFIG_PURPOSE_OPTIONS,
  GET_WHAT_WOULD_YOU_USE_DABA_FOR_STATISTICS,
} from '../resolvers';
import styles from '../analytics.module.scss';
import {formatEnumLabel} from './enumFormat';
import {InvestmentPurposeUsersTable} from './InvestmentPurposeUsersTable';

const STAT_CARD_STYLES = [
  {accent: '#1890ff', tint: '#e6f7ff', border: '#91d5ff'},
  {accent: '#52c41a', tint: '#f6ffed', border: '#b7eb8f'},
  {accent: '#722ed1', tint: '#f9f0ff', border: '#d3adf7'},
  {accent: '#fa8c16', tint: '#fff7e6', border: '#ffd591'},
  {accent: '#eb2f96', tint: '#fff0f6', border: '#ffadd2'},
  {accent: '#13c2c2', tint: '#e6fffb', border: '#87e8de'},
];

/** When using mobile-config fallback, only surface a subset of configured purposes. */
const CONFIG_PURPOSE_UI_LIMIT = 8;

export const WhatWillYouDoOverviewPage = () => {
  const [form] = Form.useForm();
  const [appliedFilters, setAppliedFilters] = useState(null);

  const {data, loading, refetch} = useQuery(
    GET_WHAT_WOULD_YOU_USE_DABA_FOR_STATISTICS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onError: error => toast.error(error.message),
    },
  );

  const statsPayload = useMemo(() => {
    const node = data?.getWhatWouldYouUseDabaForStatistics;
    if (node?.__typename === 'Error') {
      return {error: node, statistics: []};
    }
    const inner = node?.data;
    const statistics =
      inner?.__typename === 'WhatWouldYouUseDabaForStatisticsData'
        ? inner.statistics || []
        : [];
    return {error: null, statistics};
  }, [data]);

  const statsLabels = useMemo(
    () => statsPayload.statistics.map(s => s?.label).filter(Boolean),
    [statsPayload.statistics],
  );

  const {data: appConfigData} = useQuery(GET_APP_CONFIG_PURPOSE_OPTIONS, {
    skip: statsLabels.length > 0,
    fetchPolicy: 'cache-first',
    onError: error => toast.error(error.message),
  });

  const purposeOptions = useMemo(() => {
    if (statsLabels.length) {
      return statsLabels.map(label => ({
        value: label,
        label: formatEnumLabel(label),
      }));
    }

    const cfgNode = appConfigData?.getAppConfig;
    if (cfgNode?.__typename === 'Error' && cfgNode?.message) {
      return [];
    }

    const raw = appConfigData?.getAppConfig?.config?.whatWouldYouUseDabaFor;
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item, i) => {
        const value = (item?.EN || '').trim();
        const fallback = (item?.FR || `Option ${i + 1}`).trim();
        if (!value) return null;
        return {
          value,
          label:
            value.includes('_') || value === value.toUpperCase()
              ? formatEnumLabel(value)
              : value || fallback,
        };
      })
      .filter(Boolean)
      .slice(0, CONFIG_PURPOSE_UI_LIMIT);
  }, [statsLabels, appConfigData]);

  useEffect(() => {
    if (statsPayload.error?.message) {
      toast.error(statsPayload.error.message);
    }
  }, [statsPayload.error]);

  const onFinish = values => {
    const {startDate, endDate} = values || {};
    const filters = {
      startDate: startDate ? moment(startDate).format() : null,
      endDate: endDate ? moment(endDate).format() : null,
    };
    setAppliedFilters(filters);
    refetch({filters});
  };

  if (!data && loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.filterSection}>
        <Form layout="inline" onFinish={onFinish} form={form}>
          <Form.Item label="Start Date" name="startDate">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item label="End Date" name="endDate">
            <DatePicker showTime />
          </Form.Item>
          <SubmitButton label="Apply dates" smallWidth />
        </Form>
      </div>
      <div
        style={{
          marginBottom: '16px',
          fontSize: '14px',
          color: '#595959',
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderRadius: '8px',
          border: '1px solid #91d5ff',
        }}>
        <strong>Note:</strong> Start and end dates apply only to the summary
        cards above. In the users section below, choose an investment purpose to
        load the paginated table; nothing is fetched until you select one.
        Exports use the same purpose and are independent of those dates.
        <br />
        {appliedFilters?.startDate || appliedFilters?.endDate ? (
          <span>
            Currently showing filtered results from{' '}
            <strong>
              {appliedFilters?.startDate
                ? moment(appliedFilters.startDate).format('MMMM Do, YYYY')
                : moment().startOf('month').format('MMMM Do, YYYY')}
            </strong>{' '}
            to{' '}
            <strong>
              {appliedFilters?.endDate
                ? moment(appliedFilters.endDate).format('MMMM Do, YYYY')
                : moment().format('MMMM Do, YYYY')}
            </strong>
            .
          </span>
        ) : (
          <span>
            By default, showing results from{' '}
            <strong>{moment().startOf('month').format('MMMM Do, YYYY')}</strong>{' '}
            to <strong>{moment().format('MMMM Do, YYYY')}</strong>.
          </span>
        )}
      </div>

      <div className={styles.simulatorMetricsGrid}>
        {statsPayload.statistics.map((item, index) => {
          const palette = STAT_CARD_STYLES[index % STAT_CARD_STYLES.length];
          const rawVal = item?.value;
          const displayValue = (() => {
            if (rawVal == null || rawVal === '') return '—';
            const n = Number(rawVal);
            if (!Number.isNaN(n) && Number.isFinite(n)) {
              return n.toLocaleString();
            }
            return String(rawVal);
          })();

          return (
            <Spin
              spinning={loading}
              key={`${item?.label}-${index}`}
              wrapperClassName={styles.metricSpinWrapper}>
              <div
                className={`${styles.metricOverviewCard} ${styles.filteredMetricCard}`}
                style={{
                  background: palette.tint,
                  borderColor: palette.border,
                  borderLeft: `4px solid ${palette.accent}`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 160,
                }}>
                <div className={styles.dabaProTextWrapper}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 6,
                    }}>
                    <span
                      className={styles.dabaProValue}
                      style={{color: palette.accent}}>
                      {displayValue}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: palette.accent,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}>
                      {displayValue === '1' ? 'user' : 'users'}
                    </span>
                  </div>
                  <span className={styles.dabaProTitle}>
                    {item?.label ? formatEnumLabel(item.label) : '—'}
                  </span>
                  <span className={styles.dabaProSubtitle}>
                    Selected period
                  </span>
                </div>
              </div>
            </Spin>
          );
        })}
      </div>

      {statsPayload.statistics.length === 0 && !loading ? (
        <div
          style={{
            marginTop: 16,
            padding: 24,
            textAlign: 'center',
            color: '#8c8c8c',
            border: '1px dashed #d9d9d9',
            borderRadius: 8,
          }}>
          No statistics for this period.
        </div>
      ) : null}

      <InvestmentPurposeUsersTable purposeOptions={purposeOptions} />
    </div>
  );
};
